import React, { useState } from 'react'
import { AppStep, BriefingPack, ParentInput } from '../types'
import LandingPage from './LandingPage'
import ConsentStep from './ConsentStep'
import UploadStep from './UploadStep'
import ReviewStep from './ReviewStep'
import CompleteStep from './CompleteStep'
import Header from './Header'
import ProgressIndicator from './ProgressIndicator'
import RecipeApp from './recipes/RecipeApp'

export default function App() {
  // Check if we're on the recipes route
  if (window.location.pathname.startsWith('/recipes')) {
    return <RecipeApp />
  }

  const [step, setStep] = useState<AppStep>('landing')
  const [files, setFiles] = useState<File[]>([])
  const [parentInput, setParentInput] = useState<ParentInput>({
    childName: '',
    comeAlive: '',
    additionalNotes: ''
  })
  const [briefingPack, setBriefingPack] = useState<BriefingPack | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleGetStarted = () => {
    setStep('consent')
  }

  const handleConsentAccepted = () => {
    setStep('upload')
  }

  const handleFilesSelected = (newFiles: File[]) => {
    setFiles(prev => [...prev, ...newFiles])
  }

  const handleFileRemoved = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleParentInputChange = (input: Partial<ParentInput>) => {
    setParentInput(prev => ({ ...prev, ...input }))
  }

  const pollJobStatus = async (jobId: string) => {
    const maxAttempts = 60 // 60 attempts * 2 seconds = 2 minutes max
    let attempts = 0

    const poll = async (): Promise<void> => {
      if (attempts >= maxAttempts) {
        throw new Error('The briefing pack is taking longer than expected. Please try again.')
      }

      attempts++

      const response = await fetch(`/api/briefing_packs/${jobId}/status`, {
        headers: {
          'X-CSRF-Token': getCSRFToken()
        }
      })

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Job not found or has expired. Please try again.')
        }
        throw new Error('Failed to check job status. Please try again.')
      }

      const data = await response.json()

      if (data.status === 'completed') {
        setBriefingPack(data.briefing_pack)
        setStep('review')
        return
      }

      if (data.status === 'failed') {
        throw new Error(data.error || 'Failed to generate briefing pack. Please try again.')
      }

      // Still pending or processing, poll again in 2 seconds
      await new Promise(resolve => setTimeout(resolve, 2000))
      return poll()
    }

    return poll()
  }

  const handleSubmit = async () => {
    if (files.length === 0 || !parentInput.childName) return

    setIsProcessing(true)
    setError(null)

    try {
      const formData = new FormData()
      files.forEach((file) => {
        formData.append('files[]', file)
      })
      formData.append('child_name', parentInput.childName)
      formData.append('come_alive', parentInput.comeAlive)
      formData.append('additional_notes', parentInput.additionalNotes)

      const response = await fetch('/api/briefing_packs', {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': getCSRFToken()
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to generate briefing pack')
      }

      const data = await response.json()

      // Start polling for job completion
      await pollJobStatus(data.job_id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleApprove = () => {
    setStep('complete')
  }

  const handleStartOver = () => {
    setFiles([])
    setParentInput({ childName: '', comeAlive: '', additionalNotes: '' })
    setBriefingPack(null)
    setStep('consent')
  }

  // Landing page has its own layout
  if (step === 'landing') {
    return <LandingPage onGetStarted={handleGetStarted} />
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-8">
        <ProgressIndicator currentStep={step} />

        {step === 'consent' && (
          <ConsentStep onAccept={handleConsentAccepted} />
        )}

        {step === 'upload' && (
          <UploadStep
            files={files}
            parentInput={parentInput}
            onFilesSelected={handleFilesSelected}
            onFileRemoved={handleFileRemoved}
            onParentInputChange={handleParentInputChange}
            onSubmit={handleSubmit}
            isProcessing={isProcessing}
            error={error}
          />
        )}

        {step === 'review' && briefingPack && (
          <ReviewStep
            briefingPack={briefingPack}
            onApprove={handleApprove}
            onStartOver={handleStartOver}
          />
        )}

        {step === 'complete' && briefingPack && (
          <CompleteStep
            briefingPack={briefingPack}
            childName={parentInput.childName}
            onStartOver={handleStartOver}
          />
        )}
      </main>
    </div>
  )
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute('content') || ''
}
